<script setup lang="ts">

import { computed, onMounted, ref } from "vue";
import { EntityFull } from "@/domains/home/services/registry/types";
import { useRegistryStore } from "@/domains/home/services/registry/stores";
import PostalAddressView from "@/domains/home/services/nir/forms/components/widgets/viewers/PostalAddressView.vue";
import ContactView from "@/domains/home/services/nir/forms/components/widgets/viewers/ContactView.vue";
import CountryView from "@/domains/home/services/nir/forms/components/widgets/viewers/CountryView.vue";
import NatureOfBusinessISICView
  from "@/domains/home/services/nir/forms/components/widgets/viewers/NatureOfBusinessISICView.vue";
import IndividualsView from "@/domains/home/services/nir/forms/components/widgets/viewers/IndividualsView.vue";
import BeneficialOwnersView
  from "@/domains/home/services/nir/forms/components/widgets/viewers/BeneficialOwnersView.vue";
import AdminUnitsView from "@/domains/home/services/nir/forms/components/widgets/viewers/AdminUnitsView.vue";
import {
  AdminUnitsSelection, FormBeneficialOwnerPerson,
  FormContact,
  FormPerson,
  IPostalAddress,
  ISICSelection
} from "@/domains/home/services/nir/forms/components/widgets/types";
import { useRoute } from "vue-router";
import { dateFormat } from "@/commons/composables/transformations.ts";
import AppModal from "@/components/ui/AppModal.vue";
import jwtService from "@/app/services/jwt.service.js";
import { useRegistrationStatusesStore } from "@/domains/home/services/nir/configurations/stores/registrationStatuses.ts";

const store = useRegistryStore()
const registrationStatusesStore = useRegistrationStatusesStore()
const route = useRoute()
const ern = ref("")
const activeTab = ref<"details" | "affiliations" | "admin">("details");

const page = ref(1)
const limit = ref(100)
const loading = ref(false)
const errorMessage = ref('')

// Registration status modal
const showRegistrationStatus = ref(false)
const selectedRegistrationStatus = ref('')

// Account management modal
const showAccountManagement = ref(false)
const accountEmail = ref('')
const accountPhone = ref('')

const associatePartnerId = ref('')

onMounted(() =>{
  ern.value = route.params.id as string
  associatePartnerId.value = jwtService.getUser()?.['associatePartnerId'] ?? ''
  store.fetchEntity(ern.value)
  store.fetchEntityStatuses(ern.value, page.value, limit.value)
  store.fetchComplianceStatuses(associatePartnerId.value, page.value, limit.value)
  registrationStatusesStore.fetchRegistrationStatuses(1, 100)
})

const entity = computed(() => store.entity as EntityFull)

function getStatusColor(status: string) {
  return status === 'REGISTERED' || status === 'COMPLETED'
    ? 'text-green-700 font-bold bg-green-100'
    : 'text-red-700 bg-red-100'
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatValue(value: string) {
  if (!value) return '-'

  // Remove surrounding quotes from JSON-stringified values
  let cleanedValue = value
  if (value.startsWith('"') && value.endsWith('"')) {
    cleanedValue = value.slice(1, -1)
  }

  // Replace underscores with spaces and capitalize each word
  return cleanedValue
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function isSpecialField(fieldName: string): boolean {
  const specialFields = ['postal_address', 'alternative_contacts', 'country_of_residence', 'nature_of_business', 'individuals', 'beneficial_owners', 'physical_address', 'associates','representatives']
  return specialFields.includes(fieldName.toLowerCase())
}

function parseValue<T>(value: string | any): T {
  try {
    // If value is already an object, return it
    if (typeof value === 'object' && value !== null) {
      return value as T
    }
    // If value is a string, try to parse it
    if (typeof value === 'string') {
      return JSON.parse(value) as T
    }
    return value as T
  } catch (error) {
    console.error('Error parsing value:', error, value)
    return value as T
  }
}

function parseStringValue(value: string | any): string {
  try {
    // If value is already a string (not JSON), return it
    if (typeof value === 'string' && !value.startsWith('"') && !value.startsWith('{') && !value.startsWith('[')) {
      return value.toLowerCase()
    }
    // If value is a string, try to parse it
    if (typeof value === 'string') {
      return JSON.parse(value).toLowerCase() as string
    }
    return String(value).toLowerCase()
  } catch (error) {
    console.error('Error parsing string value:', error, value)
    return String(value).toLowerCase()
  }
}

// Administrative Controls


const accountEntries = computed(() => {
  const entries = store.entity?.entries ?? []
  const parse = (v: unknown): string => {
    if (typeof v !== 'string') return ''
    try { return JSON.parse(v) } catch { return v }
  }
  return {
    email: parse(entries.find((e) => e.fieldName === 'official_email')?.value),
    phone: parse(entries.find((e) => e.fieldName === 'official_phone')?.value),
  }
})

const hasAccountInfo = computed(() => !!accountEntries.value.email || !!accountEntries.value.phone)

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'An unexpected error occurred.'
}

function openRegistrationStatus() {
  selectedRegistrationStatus.value = ''
  errorMessage.value = ''
  showRegistrationStatus.value = true
}

function confirmRegistrationStatus() {
  if (!selectedRegistrationStatus.value) return
  loading.value = true
  errorMessage.value = ''
  store.setRegistrationStatus({ ern: ern.value, status: selectedRegistrationStatus.value })
    .then(() => {
      showRegistrationStatus.value = false
      store.fetchEntityStatuses(ern.value, page.value, limit.value)
      store.fetchEntity(ern.value)
    })
    .catch((err: unknown) => { errorMessage.value = extractError(err) })
    .finally(() => { loading.value = false })
}

function openAccountManagement() {
  accountEmail.value = accountEntries.value.email
  accountPhone.value = accountEntries.value.phone
  errorMessage.value = ''
  showAccountManagement.value = true
}

function saveAccount() {
  loading.value = true
  errorMessage.value = ''
  store.updateAccount({ ern: ern.value, email: accountEmail.value, phone: accountPhone.value })
    .then(() => {
      showAccountManagement.value = false
      store.fetchEntity(ern.value)
    })
    .catch((err: unknown) => { errorMessage.value = extractError(err) })
    .finally(() => { loading.value = false })
}
</script>

<template>
  <div class="flex flex-col px-6 py-3 bg-white">
    <div v-if="entity">

      <p class="text-2xl">Registry File</p>

      <div class="flex gap-2 bg-gray-50 rounded-lg p-3 my-2">
        <router-link :to="{name:'nonindividual-registry'}" class="text-primary font-semibold hover:underline">Back</router-link>
        <label>Entity / {{ entity.ern }}</label>
      </div>
      <!-- Header Section -->
      <div class="mb-6 py-4 border-b-2 border-gray-200">
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="flex flex-col">
              <h2 class="text-lg font-semibold text-primary">{{ entity.ern }}</h2>
              <h2 class="text-2xl font-bold text-gray-900">{{ entity.name }}</h2>
            </div>
            <div class="flex items-center gap-3 text-sm">
              <span class="text-white px-2 py-0.5 font-semibold bg-primary rounded-lg">
                 {{ entity.type }}
              </span>
              <span class="text-white px-2 py-0.5 font-semibold bg-success rounded-lg">
                 {{ entity.subType }}
              </span>
              <span class="flex items-center gap-1">
                <span class="text-xs text-gray-500">Registration:</span>
                <span class="text-xs font-medium px-3 py-1 rounded-full inline-block" :class="getStatusColor(entity.status)">
                  {{ entity.status.replace(/_/g, " ")}}
                </span>
              </span>
              <span class="flex items-center gap-1">
                <span class="text-xs text-gray-500">Data Completeness:</span>
                <span class="text-xs font-medium px-3 py-1 rounded-full inline-block" :class="getStatusColor(entity.recordStatus)">
                  {{ entity.recordStatus.replace(/_/g, "") }}
                </span>
              </span>
            </div>
          </div>

        </div>

        <div class="flex gap-4 text-xs text-gray-600">
          <span>
            <i class="fa-solid fa-calendar mr-1 text-gray-400"></i>
            <strong>Admitted on:</strong> {{ formatDate(entity.createdAt) }}
          </span>
          <span class="text-primary underline">
            <i class="fa-solid fa-bank"></i>
            <strong></strong> {{ entity.registrationInstitution }}
          </span>

        </div>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-gray-200 mb-4">
        <label
          @click="activeTab = 'details'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'details'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]">
          <i class="fa-solid fa-file-lines mr-2"></i>Entity Details
        </label>
        <label
          @click="activeTab = 'affiliations'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'affiliations'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]">
          <i class="fa-solid fa-link mr-2"></i>Affiliations
          <span v-if="entity.affiliations?.length"
                class="ml-1 px-1.5 py-0.5 text-xs bg-primary text-white rounded-full">
            {{ entity.affiliations.length }}
          </span>
        </label>
        <label
          @click="activeTab = 'admin'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'admin'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]">
          <i class="fa-solid fa-sliders mr-2"></i>Administrative Controls
        </label>
      </div>

      <!-- Entity Details Tab -->
      <div v-if="activeTab === 'details'">
        <div v-if="entity.entries && entity.entries.length > 0" class="space-y-4">
          <div
            v-for="(entry, idx) in entity.entries"
            :key="idx"
            class="bg-white rounded-lg border border-gray-200 p-4">
            <!-- Field Label and Updated Time -->
            <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <h4 class="text-sm font-semibold text-gray-700">
                <i class="fa-solid fa-tag mr-2 text-gray-400"></i>
                {{ entry.label }}
              </h4>
              <span v-if="entry.updatedAt" class="text-xs text-gray-500">
                <i class="fa-solid fa-clock mr-1"></i>
                {{ formatDate(entry.updatedAt) }}
              </span>
            </div>
            <!-- Special Fields with Viewer Components -->
            <div v-if="isSpecialField(entry.fieldName)">
              <PostalAddressView
                v-if="entry.fieldName.toLowerCase() === 'postal_address'"
                :data="parseValue<IPostalAddress>(entry.value)"
              />
              <ContactView
                v-else-if="entry.fieldName.toLowerCase() === 'alternative_contacts'"
                :data="parseValue<FormContact[]>(entry.value)"
              />
              <CountryView
                v-else-if="entry.fieldName.toLowerCase() === 'country_of_residence'"
                :data="parseStringValue(entry.value)"
              />
              <NatureOfBusinessISICView
                v-else-if="entry.fieldName.toLowerCase() === 'nature_of_business'"
                :data="parseValue<ISICSelection>(entry.value)"
              />
              <AdminUnitsView
                v-else-if="entry.fieldName.toLowerCase() === 'physical_address'"
                :data="parseValue<AdminUnitsSelection>(entry.value)"
              />
              <IndividualsView
                v-else-if="entry.fieldName.toLowerCase() === 'associates' || entry.fieldName.toLowerCase() === 'representatives'"
                :data="parseValue<FormPerson[]>(entry.value)"
              />
              <BeneficialOwnersView
                v-else-if="entry.fieldName.toLowerCase() === 'beneficial_owners'"
                :data="parseValue<FormBeneficialOwnerPerson[]>(entry.value)"
              />
            </div>
            <!-- Regular Fields -->
            <div v-else class="text-sm text-gray-900">
              <p class="font-medium">{{ formatValue(entry.value) }}</p>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-12">
          <i class="fa-solid fa-inbox text-5xl text-gray-300 mb-4"></i>
          <p class="text-gray-500">No registration data available for this entity.</p>
        </div>
      </div>

      <!-- Administrative Controls Tab -->
      <div v-else-if="activeTab === 'admin'" class="py-4">
        <div class="flex flex-col gap-3">

<!--          &lt;!&ndash; Registration Status &ndash;&gt;-->
<!--          <div class="border border-gray-200 rounded-lg p-4 flex items-center gap-4">-->
<!--            <div class="flex-1 min-w-0">-->
<!--              <p class="text-sm font-semibold text-gray-800">Registration Status</p>-->
<!--              <p class="text-xs text-gray-500 mt-1">Update this entity's registration status in the registry.</p>-->
<!--            </div>-->
<!--            <button-->
<!--              @click="openRegistrationStatus"-->
<!--              class="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-700 text-white text-xs font-medium rounded transition"-->
<!--            >-->
<!--              <i class="fa-solid fa-arrows-rotate"></i>-->
<!--              Update Status-->
<!--            </button>-->
<!--          </div>-->

          <!-- Account Management -->
          <div class="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-800">Account Management</p>
              <p class="text-xs text-gray-500 mt-1">View and manage the login account associated with this entity.</p>
            </div>
            <button
              @click="openAccountManagement"
              class="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-700 text-white text-xs font-medium rounded transition"
            >
              <i class="fa-solid fa-user-gear"></i>
              Manage Account
            </button>
          </div>

        </div>

        <!-- Status History -->
        <div v-if="store.entityStatuses && store.entityStatuses.length" class="mt-6">
          <h4 class="text-sm font-semibold text-gray-700 mb-3">Status History</h4>
          <table class="w-full text-xs border-collapse">
            <thead>
              <tr class="bg-gray-50 border-b border-gray-200">
                <th class="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide w-8">#</th>
                <th class="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th class="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(log, idx) in store.entityStatuses"
                :key="log.id"
                class="border-b border-gray-100 hover:bg-gray-50">
                <td class="px-3 py-2 text-gray-400 font-mono">{{ idx + 1 }}</td>
                <td class="px-3 py-2">
                  <span
                    class="px-2 py-0.5 rounded-full font-semibold"
                    :class="{
                      'bg-green-100 text-green-700': log.status === 'REGISTERED' || log.status === 'ACTIVE' || log.status === 'REACTIVATED',
                      'bg-red-100 text-red-700': log.status === 'DEREGISTERED' || log.status === 'DEACTIVATED',
                      'bg-gray-100 text-gray-600': !['REGISTERED','ACTIVE','REACTIVATED','DEREGISTERED','DEACTIVATED'].includes(log.status),
                    }"
                  >{{ log.status }}</span>
                </td>
                <td class="px-3 py-2 text-gray-600 font-mono">{{ new Date(log.createdAt).toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Affiliations Tab -->
      <div v-else-if="activeTab === 'affiliations'">
        <div v-if="entity.affiliations && entity.affiliations.length > 0">
          <table class="min-w-full leading-normal">
            <thead>
            <tr class="border-b border-t border-gray-200">
              <th>Associate Register</th>
              <th>Type</th>
              <th>Sub Type</th>
              <th>Registration Date</th>
            </tr>
            </thead>
            <tbody>
            <tr
              v-for="(affiliation, idx) in entity.affiliations"
              :key="affiliation.id"
              :class="[(idx % 2) ? 'border-t border-gray-200 bg-gray-50' : 'border-t border-gray-200']">
              <td class="text-sm">{{ affiliation.registrationInstitution }}</td>
              <td class="text-sm">{{ affiliation.type }}</td>
              <td class="text-sm">{{ affiliation.subType ? affiliation.subType : "--" }}</td>
              <td class="text-sm">{{ dateFormat(affiliation.registrationDate) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-12">
          <i class="fa-solid fa-link-slash text-5xl text-gray-300 mb-4"></i>
          <p class="text-gray-500">No affiliations found for this entity.</p>
        </div>
      </div>

    <!-- Registration Status Modal -->
    <AppModal v-model="showRegistrationStatus" sm static>
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i class="fa-solid fa-arrows-rotate text-primary"></i>
          </div>
          <h3 class="text-base font-semibold text-gray-900">Update Registration Status</h3>
        </div>
        <p class="text-sm text-gray-600">
          Select a status to apply to entity <span class="font-mono font-semibold text-gray-800">{{ ern }}</span>.
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-gray-600">Registration Status</label>
          <select
            v-model="selectedRegistrationStatus"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none bg-white"
          >
            <option value="" disabled>Select a status…</option>
            <option
              v-for="s in registrationStatusesStore.registrationStatuses"
              :key="s.id"
              :value="s.status"
            >
              {{ s.status.replace(/_/g, ' ') }}
            </option>
          </select>
        </div>
        <div v-if="errorMessage" class="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded px-3 py-2">
          <i class="fa-solid fa-circle-exclamation mt-0.5 shrink-0"></i>
          <span>{{ errorMessage }}</span>
        </div>
        <div class="flex justify-end gap-2 pt-2">
          <button @click="showRegistrationStatus = false" class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition">
            Cancel
          </button>
          <button
            @click="confirmRegistrationStatus"
            :disabled="loading || !selectedRegistrationStatus"
            class="px-4 py-2 text-sm text-white bg-primary hover:bg-primary-700 rounded transition disabled:opacity-60"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin mr-1"></i>
            Update Status
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Account Management Modal -->
    <AppModal v-model="showAccountManagement" sm static>
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i class="fa-solid fa-user-gear text-primary"></i>
          </div>
          <div>
            <h3 class="text-base font-semibold text-gray-900">Account Management</h3>
            <p class="text-xs text-gray-500 mt-0.5">Entity <span class="font-mono font-semibold text-gray-700">{{ ern }}</span></p>
          </div>
        </div>
        <div v-if="!hasAccountInfo" class="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded px-3 py-2">
          <i class="fa-solid fa-circle-info mt-0.5 shrink-0"></i>
          <span>This entity has no account information on record. Use the form below to add one.</span>
        </div>
        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600">Official Email</label>
            <input
              v-model="accountEmail"
              type="email"
              placeholder="Enter official email"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600">Official Phone</label>
            <input
              v-model="accountPhone"
              type="tel"
              placeholder="Enter official phone (e.g. 256701234567)"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none"
            />
          </div>
        </div>
        <div v-if="errorMessage" class="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded px-3 py-2">
          <i class="fa-solid fa-circle-exclamation mt-0.5 shrink-0"></i>
          <span>{{ errorMessage }}</span>
        </div>
        <div class="flex justify-end gap-2 pt-2">
          <button @click="showAccountManagement = false" class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition">
            Cancel
          </button>
          <button
            @click="saveAccount"
            :disabled="loading || (!accountEmail && !accountPhone)"
            class="px-4 py-2 text-sm text-white bg-primary hover:bg-primary-700 rounded transition disabled:opacity-60"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin mr-1"></i>
            {{ hasAccountInfo ? 'Update Account' : 'Create Account' }}
          </button>
        </div>
      </div>
    </AppModal>

    </div>

    <!-- Loading State -->
    <div v-else class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="fa-solid fa-spinner fa-spin text-4xl text-primary-600 mb-4"></i>
        <p class="text-gray-600">Loading entity data...</p>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
